import { axiosInstance } from '../lib/services/axiosinstance'
import { API_URL } from '../lib/constants'
import { useAuth } from './useAuth'

const COURSE_ENROLLMENT_API_URL = `${API_URL}/user_api/v1/enrollments/courses`
const COURSE_SESSION_ENROLLMENT_API_URL = `${API_URL}/user_api/v1/enrollments/enroll_user_on_course_session`
const COURSE_SESSION_UNENROLLMENT_API_URL = `${API_URL}/user_api/v1/enrollments/un_enroll_course_session`

const COURSE_LEARNINGPLANS_API_URL = `${API_URL}/user_api/v1/subscriptions/get_active_courses_and_sessions`
const LP_ENROLLMENT_API_URL = `${API_URL}/user_api/v1/enrollments/learning_plans`
const SUBSCRIPTIONS_API_URL = `${API_URL}/user_api/v1/subscriptions`
const SESSION_ENROLLED_USERS_API_URL = `${API_URL}/api/v1/courses/session_enrolled_users_for_branch`

export const useEnrollments = () => {
  const { getAuthHeaders } = useAuth()

  const fetchSubscriptions = async (subscriptionId?: string) => {
    try {
      const endpoint = subscriptionId
        ? `${SUBSCRIPTIONS_API_URL}?branch_id=${subscriptionId}`
        : SUBSCRIPTIONS_API_URL

      const response = await axiosInstance.get(endpoint, {
        headers: getAuthHeaders(),
      })

      if (response.status !== 200) {
        console.warn(
          `WARN: Couldn't load subscriptions: '${response.status}':'${response.statusText}'`
        )
        return []
      }

      return response.data
    } catch (error) {
      console.error('ERROR: Could not fetch subscriptions', error)
      return []
    }
  }
  const SubscriptionsActiveContent = async (catalog_ids: number[] = []) => {
    try {
      const response = await axiosInstance.post(
        COURSE_LEARNINGPLANS_API_URL,
        { catalog_ids },
        { headers: getAuthHeaders() }
      )

      return {
        courses: response.data,
      }
    } catch (error) {
      console.error('ERROR: Could not fetch courses and students', error)
      return {
        courses: [],
      }
    }
  }

  const fetchSubContentAndStudents = async (subscriptionId?: string) => {
    try {
      const coursesEndpoint = subscriptionId
        ? `${SUBSCRIPTIONS_API_URL}/${subscriptionId}/courses_and_learning_plans`
        : `${SUBSCRIPTIONS_API_URL}/courses_and_learning_plans`

      const [coursesResponse] = await Promise.all([
        axiosInstance.get(coursesEndpoint, {
          headers: getAuthHeaders(),
        }),
      ])

      if (coursesResponse.status !== 200) {
        console.warn(
          `WARN: Couldn't fetch data: Courses '${coursesResponse.status}':'${coursesResponse.statusText}'`
        )
        return { courses: [] }
      }
      return {
        courses: coursesResponse.data,
      }
    } catch (error) {
      console.error('ERROR: Could not fetch courses and students', error)
      return {
        courses: [],
      }
    }
  }

  const fetchSubContentStudents = async (subscriptionId?: string) => {
    try {
      const studentsEndpoint = `${SUBSCRIPTIONS_API_URL}/${subscriptionId}/students`
      const [studentsResponse] = await Promise.all([
        axiosInstance.get(studentsEndpoint, {
          headers: getAuthHeaders(),
        }),
      ])

      if (studentsResponse.status !== 200) {
        console.warn(
          `WARN: Couldn't fetch data: Students '${studentsResponse.status}':'${studentsResponse.statusText}'`
        )
        return { students: [] }
      }

      return {
        students: studentsResponse.data,
      }
    } catch (error) {
      console.error('ERROR: Could not fetch students', error)
      return { students: [] }
    }
  }

  const fetchSubCompareUsers = async (
    courseId?: string,
    sessionId?: string,
    branch_id?: string
  ) => {
    try {
      const response = await axiosInstance.post(
        SESSION_ENROLLED_USERS_API_URL,
        {
          course_id: courseId,
          session_id: sessionId,
          branch_id: branch_id,
        },
        { headers: getAuthHeaders() }
      )

      if (!response) {
        console.warn(`WARN: Unexpected status: ${response.status}`)
        return []
      }

      return response.data
    } catch (error) {
      console.error('ERROR: Could not fetch subscriptions', error)
      return []
    }
  }

  const enrollSessionsForLearners = async (
    courseIds: string,
    sessionId: string,
    user_ids: string[],
    transactionCode?: string
  ) => {
    try {
      const response = await axiosInstance.post(
        COURSE_SESSION_ENROLLMENT_API_URL,
        {
          enrollment: {
            course_id: courseIds,
            session_id: sessionId,
            user_ids: user_ids,
            transaction_code: transactionCode,
          },
        },
        { headers: getAuthHeaders() }
      )

      const payload = response?.data?.data?.data
      const enrolled = payload?.enrolled || []
      const errors = payload?.errors || []

      if (errors.length > 0) {
        return {
          success: false,
          enrolled,
          errors,
        }
      }

      return {
        success: true,
        enrolled,
        errors: [],
      }
    } catch (error) {
      const apiErrors = error?.response?.data?.errors
      return {
        success: false,
        errors: Array.isArray(apiErrors)
          ? apiErrors
          : [apiErrors || error.message || 'Unexpected error'],
        enrolled: [],
      }
    }
  }
  const unEnrollSessionsForLearners = async (
    courseIds: string,
    sessionId: string,
    user_id: string,
    transactionCode?: string
  ) => {
    try {
      const response = await axiosInstance.delete(
        COURSE_SESSION_UNENROLLMENT_API_URL,
        {
          headers: getAuthHeaders(),
          data: {
            enrollment: {
              items: [
                {
                  user_id,
                },
              ],
              options: {
                course_id: courseIds,
                session_id: sessionId,
                transaction_code: transactionCode,
              },
            },
          },
        }
      )

      const payload = response?.data?.data
      const entry = payload?.[0]

      if (!entry) {
        return {
          success: false,
          errors: ['Unexpected server response'],
          unenrolled: [],
        }
      }

      if (!entry.success) {
        return {
          success: false,
          errors: [entry.message],
          unenrolled: [],
        }
      }
      return {
        success: true,
        errors: [],
        unenrolled: [entry.user_id],
      }
    } catch (error) {
      const apiErrors = error?.response?.data?.errors
      return {
        success: false,
        errors: Array.isArray(apiErrors)
          ? apiErrors
          : [apiErrors || error.message || 'Unexpected error'],
        unenrolled: [],
      }
    }
  }

  const enrollCourseStudents = async (
    selectedSubscription: string,
    courseIds: string[],
    studentIds: string[]
  ) => {
    try {
      const response = await axiosInstance.post(
        COURSE_ENROLLMENT_API_URL,
        {
          subscription_id: selectedSubscription,
          course_ids: courseIds,
          user_ids: studentIds,
        },
        { headers: getAuthHeaders() }
      )

      return response.data
    } catch (error) {
      console.error('ERROR: Could not enroll students', error)
      return { error: 'Enrollment failed' }
    }
  }

  const enrollLPStudents = async (
    selectedSubscription: string,
    learningPlanIds: string[],
    studentIds: string[]
  ) => {
    try {
      const response = await axiosInstance.post(
        LP_ENROLLMENT_API_URL,
        {
          subscription_id: selectedSubscription,
          learning_plan_ids: learningPlanIds,
          user_ids: studentIds,
        },
        { headers: getAuthHeaders() }
      )

      return response.data
    } catch (error) {
      console.error('ERROR: Could not enroll students', error)
      return { error: 'Enrollment failed' }
    }
  }

  return {
    SubscriptionsActiveContent,
    fetchSubContentAndStudents,
    fetchSubContentStudents,
    fetchSubCompareUsers,
    enrollSessionsForLearners,
    unEnrollSessionsForLearners,
    enrollCourseStudents,
    enrollLPStudents,
    fetchSubscriptions,
  }
}
